import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Pencil, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";

type AttributeKind = "gauges" | "profiles" | "colors";

interface AttributeItem {
  id: number;
  value: string;
  sort_order: number;
  active: number;
}

const KIND_META: Record<AttributeKind, { title: string; singular: string }> = {
  gauges: { title: "Gauge", singular: "gauge" },
  profiles: { title: "Profile", singular: "profile" },
  colors: { title: "Color", singular: "color" },
};

export default function InventoryAttributes() {
  const { can } = useAuth();
  const canRead = can("products.read");
  const canManage = can("inventory.attributes.manage");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeKind = (params.get("tab") as AttributeKind) || "gauges";

  const [rows, setRows] = useState<Record<AttributeKind, AttributeItem[]>>({
    gauges: [],
    profiles: [],
    colors: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editSortOrder, setEditSortOrder] = useState(0);
  const [editActive, setEditActive] = useState(true);
  const [newValue, setNewValue] = useState("");
  const [newSortOrder, setNewSortOrder] = useState(0);
  const [newActive, setNewActive] = useState(true);

  const activeRows = useMemo(() => rows[activeKind] ?? [], [rows, activeKind]);
  const activeMeta = KIND_META[activeKind];
  const pg = usePagination(activeRows);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKind]);

  async function load(kind: AttributeKind) {
    const res = await api.get<{ items: AttributeItem[] }>(`/inventory/attributes/${kind}`);
    setRows((prev) => ({ ...prev, [kind]: res.items ?? [] }));
  }

  async function loadAll() {
    if (!canRead) return;
    setLoading(true);
    try {
      await Promise.all((Object.keys(KIND_META) as AttributeKind[]).map((k) => load(k)));
    } catch (err) {
      toast.error((err as Error).message || "Failed to load attributes");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canRead) return;
    if (!["gauges", "profiles", "colors"].includes(activeKind)) {
      setParams({ tab: "gauges" }, { replace: true });
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  function startEdit(row: AttributeItem) {
    setEditingId(row.id);
    setEditValue(row.value);
    setEditSortOrder(Number(row.sort_order ?? 0));
    setEditActive(Boolean(row.active));
  }

  function cancelEdit() {
    setEditingId(null);
    setEditValue("");
    setEditSortOrder(0);
    setEditActive(true);
  }

  async function saveEdit() {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.put(`/inventory/attributes/${activeKind}/${editingId}`, {
        value: editValue,
        sort_order: Number(editSortOrder || 0),
        active: editActive,
      });
      toast.success(`${activeMeta.title} updated`);
      cancelEdit();
      await load(activeKind);
    } catch (err) {
      toast.error((err as Error).message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  async function addNew() {
    if (!newValue.trim()) {
      toast.error(`${activeMeta.title} value is required`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/inventory/attributes/${activeKind}`, {
        value: newValue.trim(),
        sort_order: Number(newSortOrder || 0),
        active: newActive,
      });
      toast.success(`${activeMeta.title} added`);
      setNewValue("");
      setNewSortOrder(0);
      setNewActive(true);
      await load(activeKind);
    } catch (err) {
      toast.error((err as Error).message || "Create failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove(row: AttributeItem) {
    if (!confirm(`Delete "${row.value}"?`)) return;
    setSaving(true);
    try {
      await api.del(`/inventory/attributes/${activeKind}/${row.id}`);
      toast.success(`${activeMeta.title} deleted`);
      await load(activeKind);
    } catch (err) {
      toast.error((err as Error).message || "Delete failed");
    } finally {
      setSaving(false);
    }
  }

  if (!canRead) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">
          You do not have permission to view inventory attributes.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5 max-w-[1200px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Inventory Attributes</h1>
            <Badge variant="secondary">{activeRows.length} items</Badge>
          </div>
          <p className="text-muted-foreground">
            Manage Gauge, Profile, and Color options used by quotations.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/products">Back to Inventory</Link>
        </Button>
      </div>

      <Tabs
        value={activeKind}
        onValueChange={(next) => {
          const tab = (next as AttributeKind) || "gauges";
          setParams({ tab });
          setEditingId(null);
          navigate(`/inventory/attributes?tab=${tab}`, { replace: true });
        }}
      >
        <TabsList>
          <TabsTrigger value="gauges">Gauge</TabsTrigger>
          <TabsTrigger value="profiles">Profile</TabsTrigger>
          <TabsTrigger value="colors">Color</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="p-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Value</TableHead>
              <TableHead className="w-[140px]">Sort Order</TableHead>
              <TableHead className="w-[120px]">Active</TableHead>
              <TableHead className="w-[160px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : activeRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground">
                  No {activeMeta.singular} values yet.
                </TableCell>
              </TableRow>
            ) : (
              pg.pageItems.map((row) => {
                const isEditing = editingId === row.id;
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      {isEditing ? (
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} />
                      ) : (
                        row.value
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={String(editSortOrder)}
                          onChange={(e) => setEditSortOrder(Number(e.target.value || 0))}
                        />
                      ) : (
                        row.sort_order
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Checkbox checked={editActive} onCheckedChange={setEditActive} />
                          <span className="text-sm">{editActive ? "Active" : "Inactive"}</span>
                        </div>
                      ) : row.active ? (
                        <Badge variant="secondary">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!canManage ? null : isEditing ? (
                        <div className="inline-flex items-center gap-2">
                          <Button size="sm" onClick={saveEdit} disabled={saving}>
                            <Save className="h-4 w-4 mr-1" />
                            Save
                          </Button>
                          <Button size="icon" variant="outline" onClick={cancelEdit} disabled={saving}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2">
                          <Button size="icon" variant="outline" onClick={() => startEdit(row)} disabled={saving}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => remove(row)}
                            disabled={saving}
                            title="Delete only when unused by quotations"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {!loading && activeRows.length > 0 ? (
          <Pagination
            page={pg.page}
            pageCount={pg.pageCount}
            total={pg.total}
            from={pg.from}
            to={pg.to}
            onPrev={pg.prev}
            onNext={pg.next}
            className="-mx-4 mt-2 px-4"
          />
        ) : null}

        {canManage ? (
          <div className="mt-4 border-t pt-4 space-y-2">
            <Label>Add new {activeMeta.singular}</Label>
            <div className="grid gap-2 md:grid-cols-[1fr_140px_120px_auto]">
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={`Enter ${activeMeta.singular} value`}
              />
              <Input
                type="number"
                value={String(newSortOrder)}
                onChange={(e) => setNewSortOrder(Number(e.target.value || 0))}
                placeholder="Sort order"
              />
              <div className="flex items-center gap-2 px-2">
                <Checkbox checked={newActive} onCheckedChange={setNewActive} />
                <span className="text-sm">Active</span>
              </div>
              <Button onClick={addNew} disabled={saving}>
                Add new {activeMeta.title}
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
